import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Drawer, Form, Input, Select, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { userUnitService } from '../../services';
import './UserUnitManagement.css';

const UserUnitManagement = () => {
    const rawUser = typeof window !== 'undefined' ? localStorage.getItem('user') : null;
    const user = rawUser ? JSON.parse(rawUser) : null;
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchList();
    }, [page, limit]);

    const fetchList = async () => {
        try {
            setLoading(true);
            const res = await userUnitService.getUserUnits(page, limit);
            if (res.data.code === 200) {
                setData(res.data.data.data);
                setTotal(res.data.data.total);
            }
        } catch (err) {
            message.error('获取用户单位列表失败');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditing(null);
        form.resetFields();
        setDrawerVisible(true);
    };

    const handleEdit = (record) => {
        setEditing(record);
        form.setFieldsValue(record);
        setDrawerVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const res = await userUnitService.deleteUserUnit(id);
            if (res.data.code === 200) {
                message.success('删除成功');
                fetchList();
            }
        } catch (err) {
            message.error(err.response?.data?.message || '删除失败');
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editing) {
                const res = await userUnitService.updateUserUnit(editing.id, values);
                if (res.data.code === 200) message.success('更新成功');
            } else {
                const res = await userUnitService.createUserUnit(values);
                if (res.data.code === 200) message.success('创建成功');
            }
            setDrawerVisible(false);
            fetchList();
        } catch (err) {
            message.error(err.response?.data?.message || '操作失败');
        }
    };

    const isAdmin = user?.role === 'admin';

    const columns = [
        { title: '名称', dataIndex: 'name', key: 'name', width: 180 },
        { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: (s) => (s === 1 ? '启用' : '禁用') },
        { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 180, render: (value) => {
            if (!value) return '';
            const date = new Date(value);
            return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours()}时${date.getMinutes()}分`;
        } },
        isAdmin ? {
            title: '操作', key: 'action', width: 150, render: (_, record) => (
                <Space>
                    <Button size="small" type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Button danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>删除</Button>
                </Space>
            )
        } : null
    ];

    return (
        <div className="userunit-management">
            <Card title="用户单位管理" className="management-card">
                {isAdmin && (
                    <Space style={{ marginBottom: 16 }}>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>新增单位</Button>
                    </Space>
                )}

                <Table
                    columns={columns.filter(Boolean)}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{ current: page, pageSize: limit, total, onChange: (p) => setPage(p), onShowSizeChange: (_, s) => setLimit(s) }}
                />
            </Card>

            <Drawer title={editing ? '编辑用户单位' : '新增用户单位'} open={drawerVisible} onClose={() => setDrawerVisible(false)} width={420}>
                <Form form={form} layout="vertical" onFinish={handleSubmit} disabled={!isAdmin}>
                    <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item label="状态" name="status" initialValue={1}>
                        <Select options={[{ label: '启用', value: 1 }, { label: '禁用', value: 0 }]} />
                    </Form.Item>
                    <Form.Item>
                        {isAdmin && (
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>{editing ? '更新' : '创建'}</Button>
                        )}
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default UserUnitManagement;
